import React from 'react';
import { Heart, MapPin, Star, Users, Shield, X } from 'lucide-react';

interface WelcomeModalProps {
  onClose: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 h-[90vh] md:h-auto overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="md:flex items-center md:space-x-3 space-y-2 md:space-y-0">
            <img 
              src="/icon.svg" 
              alt="Piezit Logo" 
              className="h-8 w-8"
            />
            <h1 className="text-xl font-bold text-gray-900">Welcome to Piezit!</h1>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Discover and share positive experiences with amazing businesses in your area. 
            Help build a community of positivity and support local businesses!
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Explore Businesses</h3>
              <p className="text-sm text-gray-600">Use our interactive map to discover businesses near you</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Star className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Share Positive Reviews</h3>
              <p className="text-sm text-gray-600">Use our guided process to share your great experiences</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Build Community</h3>
              <p className="text-sm text-gray-600">Help others discover amazing businesses through your reviews</p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Verified & Trusted</h3>
              <p className="text-sm text-gray-600">All reviews are verified to ensure authenticity and trust</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary-50 to-green-50 border border-primary-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <Heart className="w-5 h-5 text-primary-600 fill-current" />
            <span className="font-medium text-primary-900">Spreading Positivity</span>
          </div>
          <p className="text-sm text-primary-800">
            Every positive review you share helps a business thrive and helps other customers 
            make informed decisions. Together, we're building a more positive world!
          </p>
        </div>

        <button
          onClick={onClose}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          <MapPin className="w-5 h-5" />
          <span>Start Exploring</span>
        </button>
      </div>
    </div>
  );
};

export default WelcomeModal;
